import { detectUnreadableFileContainer } from '@/shared/lib/detectUnreadableFileContainer'
import { MAX_UPLOAD_FILE_SIZE_BYTES } from '@/shared/lib/uploadGuardrails'
import {
  getPackagingItemDocumentUploadValidationReason,
  type PackagingItemDocumentUploadValidationReason,
} from './packagingDocumentTypes'

export type PackagingDocumentUploadIneligibilityReason =
  | PackagingItemDocumentUploadValidationReason
  | 'too-large'
  | 'encrypted'
  | 'rights-protected'
  | 'invalid-pdf'
  | 'invalid-legacy-xls'

export type PackagingDocumentUploadEligibility = {
  file: File
  eligible: boolean
  reason?: PackagingDocumentUploadIneligibilityReason
}

export function resolvePackagingDocumentUploadIneligibilityMessage(
  reason: PackagingDocumentUploadIneligibilityReason,
  messages: {
    uploadEmptyFile: string
    uploadUnsupportedFileType: string
    uploadFileTooLarge: string
    uploadEncrypted: string
    uploadRightsProtected: string
    uploadInvalidPdf: string
    uploadInvalidLegacyXls: string
  }
): string {
  if (reason === 'empty') {
    return messages.uploadEmptyFile
  }
  if (reason === 'unsupported-type') {
    return messages.uploadUnsupportedFileType
  }
  if (reason === 'too-large') {
    return messages.uploadFileTooLarge
  }
  if (reason === 'encrypted') {
    return messages.uploadEncrypted
  }
  if (reason === 'rights-protected') {
    return messages.uploadRightsProtected
  }
  if (reason === 'invalid-legacy-xls') {
    return messages.uploadInvalidLegacyXls
  }
  return messages.uploadInvalidPdf
}

export async function assessPackagingDocumentUploadEligibility(
  file: File
): Promise<PackagingDocumentUploadEligibility> {
  const validationReason = getPackagingItemDocumentUploadValidationReason(file)
  if (validationReason != null) {
    return { file, eligible: false, reason: validationReason }
  }
  if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
    return { file, eligible: false, reason: 'too-large' }
  }
  const detection = await detectUnreadableFileContainer(file)
  if (detection.kind === 'invalid-legacy-xls') {
    return { file, eligible: false, reason: 'invalid-legacy-xls' }
  }
  if (detection.kind === 'invalid-pdf') {
    return { file, eligible: false, reason: 'invalid-pdf' }
  }
  if (detection.kind === 'rights-protected') {
    return { file, eligible: false, reason: 'rights-protected' }
  }
  if (detection.kind === 'encrypted') {
    return { file, eligible: false, reason: 'encrypted' }
  }
  return { file, eligible: true }
}
